"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import * as Sentry from "@sentry/react";
import { MattersAPI } from "../../../api/matters";
import { CaseStatus } from "../../../api/types";
import {
  ListMattersParams,
  ListMattersResponse,
  MatterDTO,
  MatterStatsResponse
} from "../../../api/types/matter";

type MattersDashboardContextValue = {
  stats: MatterStatsResponse | null;
  isLoadingStats: boolean;
  statsError: Error | null;

  totalCount: number;
  items: MatterDTO[];
  filteredItems: MatterDTO[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;

  limit: number;
  offset: number;

  statusFilter: CaseStatus | "";
  visaFilter: string;
  attorneyFilter: string;
  lawFirmFilter: string;
  searchInput: string;
  sortBy: string;
  sortDir: "asc" | "desc";

  setLimit: (limit: number) => void;
  setOffset: (offset: number) => void;
  setStatusFilter: (status: CaseStatus | "") => void;
  setVisaFilter: (visa: string) => void;
  setAttorneyFilter: (attorneyUid: string) => void;
  setLawFirmFilter: (practiceName: string) => void;
  setSearch: (value: string) => void;
  setSort: (sortBy: string, sortDir: "asc" | "desc") => void;
  refresh: () => void;
  loadMore: () => void;

  canGoPrev: boolean;
  canGoNext: boolean;
};

const MattersDashboardContext =
  createContext<MattersDashboardContextValue | null>(null);

export const useMattersDashboard = () => {
  const context = useContext(MattersDashboardContext);
  if (!context) {
    throw new Error(
      "useMattersDashboard must be used within a MattersDashboardProvider"
    );
  }
  return context;
};

type ProviderProps = {
  children: React.ReactNode;
  initialLimit?: number;
};

export const MattersDashboardProvider: React.FC<ProviderProps> = ({
  children,
  initialLimit = 50
}) => {
  const [stats, setStats] = useState<MatterStatsResponse | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState<Error | null>(null);

  const [totalCount, setTotalCount] = useState(0);
  const [items, setItems] = useState<MatterDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [limit, setLimit] = useState(initialLimit);
  const [offset, setOffset] = useState(0);

  const [statusFilter, setStatusFilter] = useState<CaseStatus | "">("");
  const [visaFilter, setVisaFilter] = useState<string>("");
  const [attorneyFilter, setAttorneyFilter] = useState<string>("");
  const [lawFirmFilter, setLawFirmFilter] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearchValue] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const [refreshNonce, setRefreshNonce] = useState(0);
  const requestSeqRef = useRef(0);
  const searchTimeoutRef = useRef<number | null>(null);
  const appendNextRef = useRef(false);

  const refresh = useCallback(() => {
    setRefreshNonce((n) => n + 1);
  }, []);

  const setSearch = useCallback((value: string) => {
    setSearchInput(value);
    setOffset(0);
    appendNextRef.current = false;
    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = window.setTimeout(() => {
      setSearchValue(value);
    }, 300);
  }, []);

  const setVisaFilterAndReset = useCallback((visa: string) => {
    setVisaFilter(visa);
    setOffset(0);
    appendNextRef.current = false;
  }, []);

  const setAttorneyFilterAndReset = useCallback((uid: string) => {
    setAttorneyFilter(uid);
    setOffset(0);
    appendNextRef.current = false;
  }, []);

  const setLawFirmFilterAndReset = useCallback((practiceName: string) => {
    setLawFirmFilter(practiceName);
    setOffset(0);
    appendNextRef.current = false;
  }, []);

  const setSort = useCallback(
    (nextSortBy: string, nextSortDir: "asc" | "desc") => {
      setSortBy(nextSortBy);
      setSortDir(nextSortDir);
      setOffset(0);
      appendNextRef.current = false;
    },
    []
  );

  const fetchStats = useCallback(async () => {
    setIsLoadingStats(true);
    setStatsError(null);
    try {
      const res = await MattersAPI.stats();
      setStats(res);
    } catch (err) {
      const e =
        err instanceof Error ? err : new Error("Failed to load matter stats");
      setStats(null);
      setStatsError(e);
      Sentry.captureException(e);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  const fetchList = useCallback(async () => {
    requestSeqRef.current += 1;
    const seq = requestSeqRef.current;
    const shouldAppend = appendNextRef.current && offset > 0;
    appendNextRef.current = false;
    if (shouldAppend) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    const params: ListMattersParams = {
      limit,
      offset
    };
    if (statusFilter) {
      params.status = statusFilter;
    }
    if (visaFilter) {
      params.visa = visaFilter;
    }
    if (attorneyFilter) {
      params.lawyer_id = attorneyFilter;
    }
    if (lawFirmFilter) {
      params.practice_name = lawFirmFilter;
    }
    if (search.trim()) {
      params.q = search.trim();
    }
    if (sortBy) {
      params.sort_by = sortBy;
      params.sort_dir = sortDir;
    }

    try {
      const result: ListMattersResponse = await MattersAPI.list(
        params as ListMattersParams
      );
      if (requestSeqRef.current !== seq) return;
      setItems((prev) =>
        shouldAppend ? [...prev, ...result.matters] : result.matters
      );
      setTotalCount(
        Number.isFinite(result.total_count) ? result.total_count : 0
      );
    } catch (err) {
      if (requestSeqRef.current !== seq) return;
      const e =
        err instanceof Error ? err : new Error("Failed to load matters");
      setError(e);
      Sentry.captureException(e);
      if (!shouldAppend) {
        setItems([]);
        setTotalCount(0);
      }
    } finally {
      if (requestSeqRef.current === seq) {
        if (shouldAppend) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    }
  }, [
    limit,
    offset,
    statusFilter,
    visaFilter,
    attorneyFilter,
    lawFirmFilter,
    search,
    sortBy,
    sortDir
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshNonce]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats, refreshNonce]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        window.clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const filteredItems = items;

  const canGoPrev = false;
  const canGoNext = items.length < totalCount;

  const loadMore = useCallback(() => {
    if (canGoNext && !isLoadingMore) {
      appendNextRef.current = true;
      setOffset((n) => n + limit);
    }
  }, [canGoNext, isLoadingMore, limit]);

  const value = useMemo(
    () => ({
      stats,
      isLoadingStats,
      statsError,

      totalCount,
      items,
      filteredItems,
      isLoading,
      isLoadingMore,
      error,

      limit,
      offset,

      statusFilter,
      visaFilter,
      attorneyFilter,
      lawFirmFilter,
      searchInput,
      sortBy,
      sortDir,

      setLimit: (n: number) => {
        setLimit(Math.max(1, Math.floor(n)));
        setOffset(0);
      },
      setOffset: (n: number) => setOffset(Math.max(0, Math.floor(n))),
      setStatusFilter: (s: CaseStatus | "") => {
        setStatusFilter(s);
        setOffset(0);
      },
      setVisaFilter: setVisaFilterAndReset,
      setAttorneyFilter: setAttorneyFilterAndReset,
      setLawFirmFilter: setLawFirmFilterAndReset,
      setSearch,
      setSort,
      refresh,
      loadMore,

      canGoPrev,
      canGoNext
    }),
    [
      stats,
      isLoadingStats,
      statsError,

      items,
      filteredItems,
      isLoading,
      isLoadingMore,
      error,
      limit,
      offset,
      statusFilter,
      visaFilter,
      attorneyFilter,
      lawFirmFilter,
      searchInput,
      sortBy,
      sortDir,
      setSearch,
      setSort,
      refresh,
      loadMore,
      canGoPrev,
      canGoNext,
      setVisaFilterAndReset,
      setAttorneyFilterAndReset,
      setLawFirmFilterAndReset,
      totalCount
    ]
  );

  return (
    <MattersDashboardContext.Provider value={value}>
      {children}
    </MattersDashboardContext.Provider>
  );
};
